import { getFragmentData } from '@e-socna/data-layer';
import type { ResultOf } from '@graphql-typed-document-node/core';

import { invariant } from '@e-socna/utils';

import { useMissionContextOptional } from '../../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionContextStatus } from '../../../../contexts/MissionContext/types/mission-context.type';

import type {
  ResolverHookResult,
  ResolverParameters,
} from '../../types/resolvers.type';

import { DocumentEditorResolvers_QuickAccess_Mission_Fragment } from '../fragments/quick-access.fragment';

export const useMissionQuickAccess = ({
  enabled,
}: Readonly<ResolverParameters>): ResolverHookResult<
  ResultOf<typeof DocumentEditorResolvers_QuickAccess_Mission_Fragment>
> => {
  const missionContext = useMissionContextOptional();

  if (!enabled) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  invariant(!!missionContext, 'Mission context is required');

  const { mission, status } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      const quickAccess = getFragmentData(
        DocumentEditorResolvers_QuickAccess_Mission_Fragment,
        mission,
      );

      return {
        data: quickAccess,
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
